import { Component } from '@angular/core';
import { DataTypeComponent } from '../../../../../../../components/data-type-widgets/data-type/data-type-component';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import {
  mapRealityCheckPeriodSecondsToLabel,
  secondsToRealityCheckPeriodEnumKey,
} from '../utils/reality-check-period.utils';
import { ResponsibleGamingConfigService } from '../responsible-gaming-config.service';
import {
  LegacyResponsibleGamingDTO,
  RealityCheckConfigDTO,
  RealityCheckPeriod,
} from '../responsible-gaming-config.model';
import { FeatureFlagsService } from '../../../../feature-flags/feature-flags.service';
import { Observable } from 'rxjs';

const POSSIBLE_PERIOD_OPTIONS = Object.values(RealityCheckPeriod).filter((period) => typeof period === 'string');

@Component({
  selector: 'app-responsible-gaming-config-card',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    MatIconButton,
    MatIcon,
    MatButton,
    MatTooltip,
    MatOption,
    MatSelect,
  ],
  templateUrl: './responsible-gaming-config-card.component.html',
  styleUrl: './responsible-gaming-config-card.component.scss',
})
export class ResponsibleGamingConfigCardComponent extends DataTypeComponent {
  playerId?: number;
  realityCheckConfigDTO: RealityCheckConfigDTO;
  fg: UntypedFormGroup;
  editMode = false;
  success = false;
  updatingInProgress = false;
  dateUpdated: string;
  playerRealityCheckPeriodLabel: string;
  activitySessionV2Enabled = false;

  constructor(
    private readonly responsibleGamingConfigService: ResponsibleGamingConfigService,
    private readonly featureFlagsService: FeatureFlagsService,
    private readonly fb: UntypedFormBuilder
  ) {
    super();
  }

  override setRowData(entity: any) {
    this.playerId = entity.details?.playerId;
    this.featureFlagsService.getPlayerActivityFeatureFlags().subscribe((ff) => {
      this.activitySessionV2Enabled = !!ff.activitySessionV2Enabled;
      this.initialize();
    });
  }

  update() {
    const rawValue = this.fg.getRawValue();
    const updateConfig: RealityCheckConfigDTO = {
      playerRealityCheckPeriod:
        rawValue.playerRealityCheckPeriod === 0 ? {} : { realityCheckPeriodSeconds: rawValue.playerRealityCheckPeriod },
    };
    if (
      updateConfig.playerRealityCheckPeriod?.realityCheckPeriodSeconds !==
      this.realityCheckConfigDTO.playerRealityCheckPeriod?.realityCheckPeriodSeconds
    ) {
      this.updateConfig(updateConfig).subscribe(
        () => {
          this.toggleEditMode();
          this.fg.disable();
          this.success = true;
          setTimeout(() => {
            this.success = false;
          }, 2000);
          this.updatingInProgress = false;
        },
        () => {
          this.success = false;
          this.updatingInProgress = false;
        }
      );
    }
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.fg.enable();
    } else {
      this.initialize();
    }
  }

  periodSecondsLabel(): string {
    const period = this.realityCheckConfigDTO.playerRealityCheckPeriod?.realityCheckPeriodSeconds || 0;
    return mapRealityCheckPeriodSecondsToLabel(period);
  }

  private initialize() {
    if (this.activitySessionV2Enabled) {
      this.fetchV2Config();
    } else {
      this.fetchLegacyConfig();
    }
  }

  private fetchV2Config() {
    this.responsibleGamingConfigService.getRealityCheckConfig(this.playerId).subscribe((res) => {
      if (res) {
        this.realityCheckConfigDTO = res;
        this.initForm();
      }
    });
  }

  private fetchLegacyConfig() {
    this.responsibleGamingConfigService.getLegacyRealityCheckConfig(this.playerId).subscribe((res) => {
      this.realityCheckConfigDTO = {
        playerRealityCheckPeriod: {
          realityCheckPeriodSeconds: RealityCheckPeriod[res.realityCheckPeriod as keyof RealityCheckPeriod],
        },
      };
      this.initForm();
    });
  }

  private initForm() {
    this.playerRealityCheckPeriodLabel = this.periodSecondsLabel();
    this.fg = this.fb.group({
      playerRealityCheckPeriod: [
        this.realityCheckConfigDTO.playerRealityCheckPeriod?.realityCheckPeriodSeconds || 0,
        Validators.required,
      ],
    });
    this.fg.disable();
  }

  private updateConfig(update: RealityCheckConfigDTO): Observable<void> {
    if (this.activitySessionV2Enabled) {
      return this.responsibleGamingConfigService.updateRealityCheckConfig(this.playerId, update);
    } else {
      const updateLegacyConfig: LegacyResponsibleGamingDTO = {
        realityCheckPeriod: secondsToRealityCheckPeriodEnumKey(
          update.playerRealityCheckPeriod.realityCheckPeriodSeconds
        ),
      };
      return this.responsibleGamingConfigService.updateLegacyRealityCheckConfig(this.playerId, updateLegacyConfig);
    }
  }

  protected readonly RealityCheckPeriod = RealityCheckPeriod;
  protected readonly Object = Object;
  protected readonly POSSIBLE_PERIOD_OPTIONS = POSSIBLE_PERIOD_OPTIONS;
  protected readonly mapRealityCheckPeriodSecondsToLabel = mapRealityCheckPeriodSecondsToLabel;
}
